import type { WebResource } from '@/content/resources';
import {
  isVillageResourceKey,
  resourceForHref,
  webHrefFromResourceKey,
} from '@/lib/web-resource-href';

/** How many ranked web-available resources to show in the Popular carousel. */
export const POPULAR_ROW_LIMIT = 10;
/** Fetch enough ranks that mobile-only keys can be dropped and still fill the row. */
export const POPULAR_RANK_FETCH_LIMIT = 100;

export type PopularResourceApiItem = {
  resource_key: string;
  category: string;
  distinct_installations: number;
  tap_count: number;
};

function resourceFromWebSlug(
  key: string,
  category: string,
  catalog: WebResource[],
): WebResource | undefined {
  const slug = key.trim();
  if (!slug || slug.includes('/') || isVillageResourceKey(slug)) return undefined;
  const matches = catalog.filter((item) => item.slug === slug);
  if (matches.length === 1) return matches[0];
  if (matches.length > 1) {
    const wanted = category.trim().toLowerCase();
    return matches.find((item) => item.category.toLowerCase() === wanted) ?? matches[0];
  }
  return undefined;
}

/**
 * Resolve popular API ranks against the web catalog.
 * Drops village / unknown mobile-only keys and de-dupes by href.
 */
export function resolveWebPopularFromCatalog(
  apiItems: PopularResourceApiItem[],
  catalog: WebResource[],
): WebResource[] {
  const out: WebResource[] = [];
  const seen = new Set<string>();
  for (const row of apiItems) {
    const href = webHrefFromResourceKey(row.resource_key, catalog);
    const resource = href
      ? resourceForHref(href, catalog)
      : resourceFromWebSlug(row.resource_key, row.category, catalog);
    if (!resource || seen.has(resource.href)) continue;
    seen.add(resource.href);
    out.push(resource);
  }
  return out;
}
